import type { components } from "@/api-schema";
import { UserStatus } from "@/api-schema-enums";
import { getPhonebookProfilePath } from "@/common/helpers/get-phonebook-profile-path";
import { ContactLink } from "@/components/contact-link/contact-link";
import { DetailTabCard } from "@/components/detail-page/_components/tab/detail-tab-card";
import { DetailTabGrid } from "@/components/detail-page/_components/tab/detail-tab-grid";
import { DetailTabRow } from "@/components/detail-page/_components/tab/detail-tab-row";
import { BadgeColor, IconBadge } from "@/components/icon-badge/icon-badge";
import { EmptyValue } from "@/components/ui/empty-value";
import { UserItem, UserItemVariant } from "@/components/user-item/user-item";
import { CalculatorIcon, MailIcon, PhoneIcon, UserRoundIcon, UsersRoundIcon, UserXIcon } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

type Branch = components["schemas"]["Branch"];
type User = components["schemas"]["User"];

export function DunningTab({ branch }: { branch: Branch }): ReactElement {
  const { t } = useTranslation(["masterdata"]);

  return (
    <DetailTabGrid>
      <DetailTabCard icon={UserRoundIcon} title={t("masterdata:BranchDunningUser")}>
        <DunningContact user={branch.dunningUser} />
      </DetailTabCard>

      <DetailTabCard icon={UsersRoundIcon} title={t("masterdata:BranchDunningRepresentative")}>
        <DunningContact user={branch.dunningRepresentative} />
      </DetailTabCard>

      <DetailTabCard icon={CalculatorIcon} title={t("masterdata:BranchDunningAccounting")}>
        <DetailTabRow label={t("masterdata:BranchDunningClerkId")} value={branch.accountingDunningClerkId} mono />
      </DetailTabCard>
    </DetailTabGrid>
  );
}

function DunningContact({ user }: { user?: User | null }): ReactElement {
  const { t } = useTranslation(["masterdata", "common"]);

  if (!user) {
    return (
      <div className="py-1.5">
        <EmptyValue />
      </div>
    );
  }

  const phone = user.telephone || user.mobile;
  const isUserDisabled = user.status === UserStatus.Disabled;

  return (
    <>
      <div className="py-1.5">
        <UserItem
          user={user}
          to={`~${getPhonebookProfilePath(user.uuid)}`}
          variant={UserItemVariant.Compact}
          endContent={
            isUserDisabled ? (
              <IconBadge
                icon={UserXIcon}
                color={BadgeColor.Warning}
                tooltip={t("masterdata:BranchDunningUserInactive")}
              />
            ) : undefined
          }
        />
      </div>
      <DetailTabRow
        label={t("common:Email")}
        value={
          user.email ? <ContactLink icon={MailIcon} label={user.email} href={`mailto:${user.email}`} /> : undefined
        }
      />
      <DetailTabRow
        label={t("common:Phone")}
        value={phone ? <ContactLink icon={PhoneIcon} label={phone} href={`tel:${phone}`} /> : undefined}
      />
    </>
  );
}
